import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditOdooActivity = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    time_limit_minutes: 60,
    is_public: true,
    assigned_courses: []
  });
  
  // Course selector states
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  useEffect(() => {
    fetchActivity();
    fetchCourses();
  }, [id]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/odoo-activities/${id}`);
      const data = response.data;
      setActivity(data);
      setFormData({
        title: data.title || '',
        description: data.description || '',
        time_limit_minutes: data.time_limit_minutes || 60,
        is_public: data.is_public !== false,
        assigned_courses: data.assigned_courses || []
      });
    } catch (err) {
      console.error('Error fetching activity:', err);
      setError('Error al cargar la actividad');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setAvailableCourses(response.data.courses || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'time_limit_minutes' ? parseInt(value) || 0 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        time_limit_minutes: formData.time_limit_minutes,
        is_public: formData.is_public,
        assigned_courses: formData.is_public ? [] : formData.assigned_courses
      };

      await axios.put(`${BACKEND_URL}/api/odoo-activities/${id}`, payload);

      setSuccess('Actividad actualizada correctamente');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error updating activity:', err);
      setError(err.response?.data?.detail || 'Error al actualizar la actividad');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Cargando actividad...</div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Actividad no encontrada</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                ✏️ Editar Actividad Odoo
              </h1>
              <p className="text-white/70">
                Modifica los detalles de la actividad ERP
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all"
            >
              ← Volver
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-xl mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-200 p-4 rounded-xl mb-4">
            {success}
          </div>
        )}

        {/* Warning for non-verifiable activities */}
        {activity.is_verifiable === false && (
          <div className="bg-orange-500/20 border border-orange-500/50 rounded-xl p-4 mb-6">
            <h3 className="text-orange-200 font-semibold mb-2">
              ⚠️ Actividad con Verificación Manual
            </h3>
            <p className="text-orange-100/80 text-sm">
              {activity.verification_note || 
                'Esta actividad requiere configuración externa (credenciales de terceros) y no puede ser verificada automáticamente. Deberás revisar manualmente el trabajo del alumno.'}
            </p>
          </div>
        )}

        {/* Activity Info Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <h2 className="text-xl font-semibold text-white mb-4">
            📋 Información de la Plantilla
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white/80">
            <div>
              <span className="text-white/50 text-sm">Plantilla base:</span>
              <p className="font-medium">{activity.template_key || 'Personalizada'}</p>
            </div>
            <div>
              <span className="text-white/50 text-sm">Dificultad:</span>
              <p className="font-medium capitalize">{activity.difficulty}</p>
            </div>
            <div>
              <span className="text-white/50 text-sm">Objetivos:</span>
              <p className="font-medium">{activity.objectives?.length || 0}</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-white/5 rounded-lg">
            <span className="text-white/50 text-sm">Nota:</span>
            <p className="text-white/70 text-sm mt-1">
              Los objetivos específicos no se pueden editar ya que están definidos por la plantilla. 
              Si necesitas objetivos diferentes, crea una nueva actividad.
            </p>
          </div>
        </div>

        {/* Edit Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <h2 className="text-xl font-semibold text-white mb-4">
            ⚙️ Configuración Editable
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/80 mb-2 text-sm">Título</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
                required
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2 text-sm">Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2 text-sm">
                Tiempo límite (minutos)
              </label>
              <input
                type="number"
                name="time_limit_minutes"
                value={formData.time_limit_minutes}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
              />
              <p className="text-white/50 text-xs mt-1">
                0 = Sin límite de tiempo
              </p>
            </div>

            {/* Public/Private Assignment Section */}
            <div className="border-t border-white/20 pt-4 mt-4">
              <label className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-white/30 text-purple-500 focus:ring-purple-500 bg-white/10"
                />
                <span className="text-white font-medium">Actividad Pública</span>
                <span className="text-white/50 text-sm">- Cualquier estudiante puede acceder</span>
              </label>

              {!formData.is_public && (
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <label className="block text-white/80 mb-3 text-sm">
                    📚 Asignar a Clases de Google Classroom
                  </label>
                  
                  <button
                    type="button"
                    onClick={() => setShowCourseSelector(!showCourseSelector)}
                    className="px-4 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition flex items-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {formData.assigned_courses.length > 0 
                      ? `${formData.assigned_courses.length} clase(s) asignada(s)` 
                      : 'Seleccionar clases'}
                  </button>

                  {showCourseSelector && (
                    <div className="mt-3 p-3 bg-black/20 rounded-lg max-h-48 overflow-y-auto">
                      {loadingCourses ? (
                        <p className="text-white/60 text-sm">Cargando clases...</p>
                      ) : availableCourses.length === 0 ? (
                        <p className="text-white/60 text-sm">No hay clases disponibles. Conecta Google Classroom primero.</p>
                      ) : (
                        <div className="space-y-2">
                          {availableCourses.map((course) => (
                            <label key={course.id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.assigned_courses.includes(course.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      assigned_courses: [...prev.assigned_courses, course.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      assigned_courses: prev.assigned_courses.filter(id => id !== course.id)
                                    }));
                                  }
                                }}
                                className="w-4 h-4 rounded border-white/30 text-purple-500 focus:ring-purple-500 bg-white/10"
                              />
                              <span className="text-white/90 text-sm">{course.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected courses display */}
                  {formData.assigned_courses.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.assigned_courses.map((courseId) => {
                        const course = availableCourses.find(c => c.id === courseId);
                        return course ? (
                          <span key={courseId} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-500/30 text-purple-200 border border-purple-400/30">
                            {course.name}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                assigned_courses: prev.assigned_courses.filter(id => id !== courseId)
                              }))}
                              className="ml-2 hover:text-white"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className={`w-full py-3 rounded-xl font-semibold transition-all ${
                  saving
                    ? 'bg-gray-500/50 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {saving ? '⏳ Guardando...' : '💾 Guardar Cambios'}
              </button>
            </div>
          </form>
        </div>

        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-200 mb-2">
            💡 ¿Qué se puede editar?
          </h3>
          <ul className="text-blue-100/80 space-y-2 text-sm">
            <li>• <strong>Título y descripción:</strong> Personaliza cómo se muestra la actividad a los alumnos</li>
            <li>• <strong>Tiempo límite:</strong> Ajusta el tiempo máximo para completar la actividad</li>
            <li>• <strong>Visibilidad:</strong> Cambia entre pública (todos) o privada (clases específicas)</li>
            <li>• <strong>Los objetivos</strong> no se pueden editar porque dependen de la plantilla base</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EditOdooActivity;
